"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Container,
  Grid,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Stack,
} from "@mui/material"
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { IonContent, IonPage } from "@ionic/react"
import { baseURL, config, requestID } from "../../common/utils"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

const AlertCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  border: "1px solid #fecaca",
  backgroundColor: "#fef2f2",
  marginBottom: theme.spacing(2),
}))

type Product = {
  alerted_at: string
  id: number
  current_stock: string
  product_name: string
  product_id: string
  resolved: boolean
}

function NotificationAlert() {
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filter, setFilter] = useState<"all" | "critical" | "low" | "resolved">("all")

  const fetchAlerts = async () => {
    setLoading(true)
    try {
      const payload = { requestID: requestID() }
      const response = await axios.post(`${baseURL}warehouses/getReorderAlerts`, payload, config)
      console.log("Stock alert response:", response.data)
      setProducts(response.data.data || [])
    } catch (error) {
      console.error("Failed to fetch stock alerts:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAlerts()
  }, [])

  useEffect(() => {
    let filtered = products.filter(
      (product) =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.product_id.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    // Apply filter
    switch (filter) {
      case "critical":
        filtered = filtered.filter((p) => Number.parseInt(p.current_stock) === 0)
        break
      case "low":
        filtered = filtered.filter(
          (p) => Number.parseInt(p.current_stock) > 0 && Number.parseInt(p.current_stock) <= 10,
        )
        break
      case "resolved":
        filtered = filtered.filter((p) => p.resolved)
        break
      default:
        break
    }

    setFilteredProducts(filtered)
    setPage(0)
  }, [searchTerm, products, filter])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStockStatus = (stock: string) => {
    const stockNum = Number.parseInt(stock)
    if (stockNum === 0) return { label: "Out of Stock", color: "error", severity: "critical" }
    if (stockNum <= 5) return { label: "Critical Low", color: "error", severity: "critical" }
    if (stockNum <= 10) return { label: "Low Stock", color: "warning", severity: "low" }
    return { label: "Normal", color: "success", severity: "normal" }
  }

  const getStockIcon = (stock: string) => {
    const stockNum = Number.parseInt(stock)
    if (stockNum === 0) return <ErrorIcon sx={{ color: "#ef4444" }} />
    if (stockNum <= 10) return <WarningIcon sx={{ color: "#f59e0b" }} />
    return <CheckCircleIcon sx={{ color: "#10b981" }} />
  }

  const getCriticalAlerts = () => products.filter((p) => Number.parseInt(p.current_stock) === 0).length
  const getLowStockAlerts = () =>
    products.filter((p) => Number.parseInt(p.current_stock) > 0 && Number.parseInt(p.current_stock) <= 10).length
  const getResolvedAlerts = () => products.filter((p) => p.resolved).length
  const getTotalAlerts = () => products.length

  const paginatedProducts = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color="#1e293b"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <NotificationsIcon />
                    Stock Alerts
                  </Typography>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                    Monitor critical stock levels and reorder alerts
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchAlerts}
                  disabled={loading}
                  sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                >
                  Refresh
                </Button>
              </Box>
            </HeaderCard>
          </StyledCard>

          {/* Critical Alerts Banner */}
          {getCriticalAlerts() > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              icon={<ErrorIcon />}
              action={
                <IconButton size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body1" fontWeight="600">
                {getCriticalAlerts()} products are completely out of stock and require immediate attention!
              </Typography>
            </Alert>
          )}

          {/* Stats Dashboard */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <NotificationsIcon sx={{ fontSize: 32, mb: 1, color: "#3b82f6" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getTotalAlerts()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Alerts
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <ErrorIcon sx={{ fontSize: 32, mb: 1, color: "#ef4444" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getCriticalAlerts()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Critical Alerts
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <WarningIcon sx={{ fontSize: 32, mb: 1, color: "#f59e0b" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getLowStockAlerts()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Low Stock
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <CheckCircleIcon sx={{ fontSize: 32, mb: 1, color: "#10b981" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getResolvedAlerts()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Resolved
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Main Content */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              {/* Search and Filters */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder="Search by product name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        variant={filter === "all" ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setFilter("all")}
                        sx={{
                          bgcolor: filter === "all" ? "#1e293b" : "transparent",
                          color: filter === "all" ? "white" : "#64748b",
                          borderColor: "#d1d5db",
                          "&:hover": { bgcolor: filter === "all" ? "#334155" : "#f8fafc" },
                        }}
                      >
                        All ({getTotalAlerts()})
                      </Button>
                      <Button
                        variant={filter === "critical" ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setFilter("critical")}
                        sx={{
                          bgcolor: filter === "critical" ? "#ef4444" : "transparent",
                          color: filter === "critical" ? "white" : "#ef4444",
                          borderColor: "#ef4444",
                          "&:hover": { bgcolor: filter === "critical" ? "#dc2626" : "#fef2f2" },
                        }}
                      >
                        Critical ({getCriticalAlerts()})
                      </Button>
                      <Button
                        variant={filter === "low" ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setFilter("low")}
                        sx={{
                          bgcolor: filter === "low" ? "#f59e0b" : "transparent",
                          color: filter === "low" ? "white" : "#f59e0b",
                          borderColor: "#f59e0b",
                          "&:hover": { bgcolor: filter === "low" ? "#d97706" : "#fefbf2" },
                        }}
                      >
                        Low Stock ({getLowStockAlerts()})
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Table */}
              {loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="#64748b" sx={{ mt: 2 }}>
                    Loading stock alerts...
                  </Typography>
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <NotificationsIcon sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                  <Typography variant="h6" color="#64748b" sx={{ mb: 1 }}>
                    {searchTerm || filter !== "all" ? "No matching alerts found" : "No stock alerts"}
                  </Typography>
                  <Typography variant="body2" color="#94a3b8">
                    {searchTerm || filter !== "all"
                      ? "Try adjusting your search or filter criteria"
                      : "All products have adequate stock levels"}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Current Stock</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Alert Date</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="center">
                            Resolved
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedProducts.map((item, index) => {
                          const stockStatus = getStockStatus(item.current_stock)
                          return (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {getStockIcon(item.current_stock)}
                                  <Box>
                                    <Typography variant="body1" fontWeight="500" color="#1e293b">
                                      {item.product_name}
                                    </Typography>
                                    <Typography variant="caption" color="#64748b">
                                      ID: {item.product_id}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body1"
                                  fontWeight="600"
                                  sx={{
                                    color:
                                      stockStatus.severity === "critical"
                                        ? "#ef4444"
                                        : stockStatus.severity === "low"
                                          ? "#f59e0b"
                                          : "#10b981",
                                  }}
                                >
                                  {item.current_stock} units
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={stockStatus.label}
                                  color={stockStatus.color as any}
                                  size="small"
                                  icon={getStockIcon(item.current_stock)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="#64748b">
                                  {formatDate(item.alerted_at)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={item.resolved ? "Yes" : "No"}
                                  color={item.resolved ? "success" : "error"}
                                  size="small"
                                  variant={item.resolved ? "filled" : "outlined"}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  <TablePagination
                    component="div"
                    count={filteredProducts.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(Number.parseInt(e.target.value, 10))
                      setPage(0)
                    }}
                    sx={{ borderTop: "1px solid #e2e8f0", mt: 2 }}
                  />
                </>
              )}
            </CardContent>
          </StyledCard>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default NotificationAlert
